import { Controller } from '@hotwired/stimulus';
import { Helpers } from '../helpers';
import * as bootstrap from 'bootstrap';

export default class InvestFormController extends Controller {
  static targets = [
    'subscriptionAmount',
    'feesAmount',
    'fieldsErrors',
    'subscriptionAmountDivs',
    'feesAmountDivs',
    'totalAmountDivs',
    'nonInformedFields',
    'modalTotalWithFees',
    'submitButton',
    'insufficientBalance',
    'subscribeModal',
  ];

  static values = {
    feesUrl: String,
    projectId: Number,
    profileBalance: Number,
    maxInvestRecommendation: Number,
    userMayHaveWarning: Boolean,
  };

  // Compute fees and total through backend
  async computeFeesAndTotal() {
    const amount = this.subscriptionAmountTarget.value;
    const params = new URLSearchParams({
      amount,
      project_id: this.projectIdValue,
    });

    try {
      const response = await fetch(`${this.feesUrlValue}?${params}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      const feesAmount = this.#parseFloatSafe(data['fees_amount']);
      const totalWithFeesAmount = this.#parseFloatSafe(data['total_with_fees']);

      this.checkAmountValidity(amount, totalWithFeesAmount);

      this.feesAmountTarget.value = feesAmount;
      this.updateAmounts(this.subscriptionAmountDivsTargets, this.#parseFloatSafe(amount));
      this.updateAmounts(this.feesAmountDivsTargets, feesAmount);
      this.updateAmounts(this.totalAmountDivsTargets, totalWithFeesAmount);
      this.modalTotalWithFeesTarget.innerHTML =
        Helpers.numberToCurrency(totalWithFeesAmount, 2);
    } catch {
      this.fieldsErrorsTarget.innerHTML =
        "Une erreur s'est produite, veuillez réessayer.";
    }
  }

  checkAmountValidity(amount, totalWithFeesAmount) {
    this.insufficientBalanceTarget.classList.add('d-none');

    const profileBalance = this.#parseFloatSafe(this.profileBalanceValue);

    switch (true) {
      case !this.subscriptionAmountTarget.checkValidity():
        this.fieldsErrorsTarget.innerHTML =
          this.subscriptionAmountTarget.validationMessage;
        return this.triggerError();
      case amount === '':
        this.fieldsErrorsTarget.innerHTML =
          'Un montant de souscription doit être renseigné.';
        return this.triggerError();
      case totalWithFeesAmount > profileBalance:
        this.insufficientBalanceTarget.classList.remove('d-none');
        this.fieldsErrorsTarget.innerHTML = '';
        return this.triggerError();
      default:
        this.fieldsErrorsTarget.innerHTML = '';
        this.subscriptionAmountTarget.classList.remove('is-invalid');
        return true;
    }
  }

  updateAmounts(container, amount) {
    const finalAmount =
      amount === 0 || isNaN(amount)
        ? '-,-- €'
        : Helpers.numberToCurrency(amount, 2);

    container.forEach((element) => {
      element.innerHTML = finalAmount;
    });
  }

  triggerError() {
    this.subscriptionAmountTarget.classList.add('is-invalid');
    return false;
  }

  validateAndShowSubscriptionModal() {
    const amount = this.subscriptionAmountTarget.value;
    const maxInvestRecommendation = this.maxInvestRecommendationValue;
    const totalWithFeesAmount =
      this.#parseFloatSafe(amount) + this.#parseFloatSafe(this.feesAmountTarget.value);

    if (!this.checkAmountValidity(amount, totalWithFeesAmount)) return;

    const showWarning =
      this.userMayHaveWarningValue === true &&
      amount > maxInvestRecommendation;

    this.nonInformedFieldsTargets.forEach((element) => {
      element.classList.toggle('d-none', !showWarning);
      element.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.required = showWarning;
      });
    });

    this.showModal(this.subscribeModalTarget);
  }

  preventAutoSubmit(event) {
    event.preventDefault();
    this.validateAndShowSubscriptionModal();
  }

  showModal(modalTarget) {
    const modal = new bootstrap.Modal(modalTarget);
    modal.show();
  }

  teardownModal() {
    const modal = bootstrap.Modal.getInstance(this.subscribeModalTarget);
    if (modal) {
      modal.hide();
      modal.dispose();
    } else {
      this.subscribeModalTarget.classList.remove('show');
      this.subscribeModalTarget.removeAttribute('style');
      document.body.classList.remove('modal-open');
      document.body.removeAttribute('style');
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    }
  }

  #parseFloatSafe(value) {
    return parseFloat(value ?? 0);
  }
}
